import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import { logAnalyticsEvent } from "../../Firebase";
import { CASE_STUDIES } from "../../data/caseStudies";
import "./Public.css";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const cs = useMemo(() => CASE_STUDIES.find((x) => x.slug === slug), [slug]);

  useSeo({
    title: cs
      ? `${cs.title} | Case Study | Crowned Roofing`
      : "Case Study | Crowned Roofing",
    description:
      cs?.summary ||
      "Project case study documenting inspection findings, scope development, and outcomes.",
    ogImage: cs?.heroImg
      ? `https://www.crownedroofing.com${cs.heroImg}`
      : "https://www.crownedroofing.com/images/ogimg.png",
    canonical: `https://www.crownedroofing.com/case-studies/${slug}`,
  });

  if (!cs) {
    return (
      <div className="csdPage">
        <Header />
        <div className="csdContainer csdNotFound">
          <h1>Case study not found</h1>
          <p>That case study link may be outdated. Return to the case studies list.</p>
          <button className="csdBtn csdBtnPrimary" onClick={() => navigate("/portfolio")}>
            Back to Case Studies
          </button>
        </div>
      </div>
    );
  }

  const openContact = () => {
    logAnalyticsEvent("case_study_detail_cta_click", { slug: cs.slug });
    navigate("/contact");
  };

  return (
    <div className="csdPage">
      <Header />

      {/* HERO */}
      <section className="csdHero">
        <div className="csdHeroBg" style={{ backgroundImage: `url(${cs.heroImg})` }} aria-hidden="true" />
        <div className="csdHeroShade" aria-hidden="true" />

        <div className="csdHeroInner">
          <div className="csdContainer">
            <button className="csdBack" onClick={() => navigate("/portfolio")}>
              ← Back to Case Studies
            </button>

            <div className="csdHeroCard">
              <div className={`csdPill ${cs.type}`}>{cs.type === "commercial" ? "Commercial" : "Residential"}</div>
              <h1 className="csdTitle">{cs.title}</h1>
              <div className="csdLoc">{cs.location}</div>
              <p className="csdSummary">{cs.summary}</p>

              <div className="csdTags">
                {cs.tags?.map((t) => (
                  <span className="csdTag" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="csdBody">
        <div className="csdContainer csdGrid">
          {/* MAIN */}
          <div className="csdMain">
            <div className="csdSection">
              <h2>Situation</h2>
              <p>{cs.situation}</p>
            </div>

            <div className="csdSection">
              <h2>Inspection Findings</h2>
              <ul>
                {cs.findings?.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>

            <div className="csdSection">
              <h2>Scope / Approach</h2>
              <p>{cs.scope}</p>
            </div>

            <div className="csdSection">
              <h2>Outcome</h2>
              <p>{cs.outcome}</p>
            </div>

            {cs.photos?.length ? (
              <div className="csdSection">
                <h2>Photos</h2>
                <div className="csdPhotos">
                  {cs.photos.map((src, idx) => (
                    <div className="csdPhoto" key={`${src}-${idx}`}>
                      <img src={src} alt="" aria-hidden="true" />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* SIDEBAR */}
          <aside className="csdSide">
            <div className="csdFacts">
              <div className="csdFactsTitle">Key Facts</div>

              <div className="csdFact">
                <div className="k">Building Type</div>
                <div className="v">{cs.facts?.buildingType || "—"}</div>
              </div>
              <div className="csdFact">
                <div className="k">Roof System</div>
                <div className="v">{cs.facts?.roofType || "—"}</div>
              </div>
              <div className="csdFact">
                <div className="k">Approx. Area</div>
                <div className="v">{cs.facts?.approxArea || "—"}</div>
              </div>
              <div className="csdFact">
                <div className="k">Access</div>
                <div className="v">{cs.facts?.access || "—"}</div>
              </div>
              <div className="csdFact">
                <div className="k">Deliverable</div>
                <div className="v">{cs.facts?.deliverable || "—"}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CTA */}
      <section className="csdCta">
        <div className="csdContainer">
          <div className="csdCtaCard">
            <h2>Need clarity on roof condition?</h2>
            <p>
              If you’re deciding between targeted repairs, maintenance planning, or replacement timing, we can evaluate
              existing conditions and define a scope based on documented findings.
            </p>
            <button className="csdBtn csdBtnPrimary" onClick={openContact}>
              REQUEST AN ASSESSMENT
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
